import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import documentService from '../services/documentService';
import { useNotification } from '../context/NotificationContext';
import '../styles/general.css';

const ALLOWED_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png'];
const MAX_BYTES = 10 * 1024 * 1024;

/**
 * Replace one company document's file.
 *
 * The checks below duplicate the serializer's on purpose: they exist to give
 * a fast answer, not to be the guarantee. The backend re-checks every one of
 * them, including the file's magic bytes, which the browser cannot see.
 */
const ReplaceDocumentModal = ({ slug, onClose, onReplaced }) => {
  const { t } = useTranslation();
  const { notify } = useNotification();

  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // A slot's leftover selection must not follow the user to the next slot.
  useEffect(() => {
    setFile(null);
    setError(null);
  }, [slug]);

  if (!slug) return null;

  const handleChange = (event) => {
    const chosen = event.target.files?.[0] || null;
    setFile(null);
    setError(null);

    if (!chosen) return;

    const extension = chosen.name.split('.').pop().toLowerCase();

    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      setError(t('files.invalidType'));
      return;
    }

    if (chosen.size > MAX_BYTES) {
      setError(t('files.tooLarge'));
      return;
    }

    setFile(chosen);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await documentService.replaceDocument(slug, file);
      onReplaced();
    } catch (err) {
      console.error('Error replacing company document:', err);
      notify.error(t('files.failedToReplace'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title={t('files.replaceTitle')}>
      <div className="form-group">
        <p className="text-bold">{t(`files.documents.${slug}`)}</p>
        {/* Stated before the action, not after: there is no history and no
            way back once the file is overwritten. */}
        <p className="text-muted">{t('files.replaceWarning')}</p>
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="replace-document-input">
          {t('files.chooseFile')}
        </label>
        <input
          id="replace-document-input"
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          onChange={handleChange}
          className="form-input"
        />
        {error && <div className="form-error">{error}</div>}
      </div>

      <div className="form-actions">
        <button type="button" onClick={onClose} className="btn btn-secondary">
          {t('common.cancel')}
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!file || saving}
          className="btn btn-primary"
        >
          {t('common.save')}
        </button>
      </div>
    </Modal>
  );
};

export default ReplaceDocumentModal;
